import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDate, parseDateInputToIso } from './formatting';

/**
 * Datumski input — UVIJEK dd.mm.yyyy (BI foundation pravilo).
 * Tekstualni unos (ručno tipkanje) + kalendar-popup (klik na 📅). Nikad native <input type="date">.
 * Parent state ostaje ISO yyyy-mm-dd; konverzija samo na UI granici.
 * Kalendar se renderira u PORTALU (position:fixed, visok z-index) da ne bude skriven iza
 * susjednih kartica/overflow-a.
 */
const HR_DOW = ['Po', 'Ut', 'Sr', 'Če', 'Pe', 'Su', 'Ne'];
const HR_MON = ['Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];

const POPUP_W = 234;

function isoOf(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DateInput({
    value,
    onChange,
    required,
    disabled,
    style,
    className,
    placeholder = 'dd.mm.yyyy',
    onBlur,
    name,
    id,
}: {
    value: string;
    onChange: (iso: string) => void;
    required?: boolean;
    disabled?: boolean;
    style?: React.CSSProperties;
    className?: string;
    placeholder?: string;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    name?: string;
    id?: string;
}) {
    const [text, setText] = useState(() => (value ? formatDate(value, 'hr') : ''));
    const [open, setOpen] = useState(false);
    const [view, setView] = useState<Date>(() => {
        const d = value ? new Date(value + 'T00:00:00') : new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const wrapRef = useRef<HTMLSpanElement | null>(null);
    const popRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const reposition = () => {
        const r = wrapRef.current?.getBoundingClientRect();
        if (!r) return;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - POPUP_W - 8));
        setCoords({ top: r.bottom + 4, left });
    };

    useEffect(() => {
        setText(value ? formatDate(value, 'hr') : '');
    }, [value]);

    useLayoutEffect(() => {
        if (open) reposition();
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onDoc = (e: MouseEvent) => {
            const tgt = e.target as Node;
            if (wrapRef.current?.contains(tgt) || popRef.current?.contains(tgt)) return;
            setOpen(false);
        };
        const onScrollResize = () => reposition();
        document.addEventListener('mousedown', onDoc);
        window.addEventListener('scroll', onScrollResize, true);
        window.addEventListener('resize', onScrollResize);

        return () => {
            document.removeEventListener('mousedown', onDoc);
            window.removeEventListener('scroll', onScrollResize, true);
            window.removeEventListener('resize', onScrollResize);
        };
    }, [open]);

    function commit(raw: string) {
        const trimmed = raw.trim();
        if (trimmed === '') {
            onChange('');
            return;
        }
        const iso = parseDateInputToIso(trimmed);
        if (iso) {
            onChange(iso);
        }
    }

    function toggleCal() {
        if (disabled) {
            return;
        }
        const d = value ? new Date(value + 'T00:00:00') : new Date();
        setView(new Date(d.getFullYear(), d.getMonth(), 1));
        setOpen((o) => !o);
    }

    function pick(iso: string) {
        onChange(iso);
        setText(iso ? formatDate(iso, 'hr') : '');
        setOpen(false);
    }

    const startDow = (new Date(view.getFullYear(), view.getMonth(), 1).getDay() + 6) % 7; // Pon = 0
    const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const cells: Array<number | null> = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const todayIso = isoOf(new Date());

    const popup = (
        <div ref={popRef} style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 10000, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 8, width: POPUP_W }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--ad-ink)', padding: '0 6px' }}>‹</button>
                <strong style={{ fontSize: '0.85rem', color: 'var(--ad-ink)' }}>{HR_MON[view.getMonth()]} {view.getFullYear()}.</strong>
                <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--ad-ink)', padding: '0 6px' }}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
                {HR_DOW.map((w) => <div key={w} style={{ textAlign: 'center', fontSize: '0.66rem', color: '#9a8a55', fontWeight: 600 }}>{w}</div>)}
                {cells.map((d, i) => {
                    if (d === null) {
                        return <div key={i} />;
                    }
                    const iso = isoOf(new Date(view.getFullYear(), view.getMonth(), d));
                    const sel = iso === value;
                    const isToday = iso === todayIso;
                    return (
                        <button key={i} type="button" onClick={() => pick(iso)}
                            style={{ textAlign: 'center', fontSize: '0.78rem', padding: '4px 0', borderRadius: 6, border: isToday && !sel ? '1px solid var(--ad-acc)' : '1px solid transparent', cursor: 'pointer', background: sel ? 'var(--ad-acc)' : 'transparent', color: sel ? '#fff' : '#3a2f17', fontWeight: sel ? 700 : 400 }}>
                            {d}
                        </button>
                    );
                })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <button type="button" onClick={() => pick(todayIso)} style={{ fontSize: '0.72rem', border: 'none', background: 'none', color: 'var(--ad-ink)', cursor: 'pointer' }}>Danas</button>
                <button type="button" onClick={() => pick('')} style={{ fontSize: '0.72rem', border: 'none', background: 'none', color: '#b14040', cursor: 'pointer' }}>Očisti</button>
            </div>
        </div>
    );

    // Wrapper UVIJEK uzima 100% širine roditelja (default), pa global CSS poput
    // `.filter-label > input { width:100% }` ne istisne 📅 gumb van. Input flex-uje
    // unutar wrappera; ako parent prosljedi width preko `style`, taj nadjača 100%.
    const widthFromStyle = (style as React.CSSProperties | undefined)?.width;
    const inputStyle: React.CSSProperties = { ...(style ?? {}) };
    delete inputStyle.width;
    return (
        // CSS Grid (1fr auto): input dobije 1fr, gumb auto. Bulletproof — wrapper uvijek 100% ćelije, button stane.
        <span ref={wrapRef} style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 4, width: widthFromStyle ?? '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <input
                type="text"
                inputMode="numeric"
                value={text}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                style={{ minWidth: 0, width: '100%', boxSizing: 'border-box', ...inputStyle }}
                className={className}
                name={name}
                id={id}
                onChange={(e) => setText(e.target.value)}
                onBlur={(e) => { commit(e.target.value); onBlur?.(e); }}
            />
            <button
                type="button"
                disabled={disabled}
                onClick={toggleCal}
                aria-label="Otvori kalendar"
                title="Kalendar"
                style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fffdf6', borderRadius: 6, cursor: disabled ? 'default' : 'pointer', padding: '2px 8px', lineHeight: 1, fontSize: '0.95rem', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap' }}
            >📅</button>
            {open ? createPortal(popup, document.body) : null}
        </span>
    );
}
